import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowRight, CheckCircle, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackBetaSignupError, trackBetaSignupStart, trackBetaSignupSuccess } from '../utils/analytics';
import './BetaSignupForm.css';

interface BetaSignupFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  motivation: string;
  deviceType: 'iphone' | 'android' | 'both' | 'other';
  agreeTerms: boolean;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  motivation: '',
  deviceType: 'iphone',
  agreeTerms: false,
};

const isLocalDevelopment = () => (
  typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname)
);

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();
const recaptchaRequired = import.meta.env.VITE_REQUIRE_CAPTCHA !== 'false';

const BetaSignupForm: React.FC<BetaSignupFormProps> = ({ isOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isLocal = isLocalDevelopment();

  React.useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    trackBetaSignupStart('modal_open');

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox'
        ? (event.target as HTMLInputElement).checked
        : value,
    }));
  };

  const validateForm = (): string | null => {
    if (formData.name.trim().length < 2) return t('beta_signup.validation.name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      return t('beta_signup.validation.email');
    }
    if (formData.motivation.trim().length < 10) {
      return t('beta_signup.validation.motivation');
    }
    if (!formData.agreeTerms) return t('beta_signup.validation.terms');
    if (!isLocal && recaptchaRequired && !recaptchaSiteKey) {
      return t('beta_signup.validation.unavailable');
    }
    if (!isLocal && recaptchaRequired && !recaptchaValue) {
      return t('beta_signup.validation.captcha');
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: '',
          experience: '',
          iosVersion: '',
          recaptchaResponse: isLocal || !recaptchaRequired
            ? 'captcha-not-required'
            : recaptchaValue,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('beta_signup.errors.save'));
      }

      setSubmitStatus('success');
      trackBetaSignupSuccess();
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('beta_signup.errors.generic');
      setSubmitStatus('error');
      setErrorMessage(message);
      trackBetaSignupError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setSubmitStatus('idle');
    setErrorMessage('');
    setRecaptchaValue(null);
    recaptchaRef.current?.reset();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="beta-signup__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleClose();
          }}
        >
          <motion.section
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-signup-title"
            className="beta-signup"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="beta-signup__close"
              aria-label={t('beta_signup.close_aria')}
            >
              <X size={20} />
            </button>

            {submitStatus === 'success' ? (
              <div className="beta-signup__success">
                <span className="beta-signup__success-icon"><CheckCircle size={28} /></span>
                <p className="beta-signup__kicker">{t('beta_signup.success.kicker')}</p>
                <h2 id="beta-signup-title">{t('beta_signup.success.title')}</h2>
                <p>{t('beta_signup.success.description', { email: formData.email })}</p>
                <button type="button" onClick={handleClose} className="beta-signup__primary">
                  {t('beta_signup.success.button')}
                </button>
              </div>
            ) : (
              <>
                <header className="beta-signup__header">
                  <p className="beta-signup__kicker">{t('beta_signup.header.kicker')}</p>
                  <h2 id="beta-signup-title">{t('beta_signup.header.title')}</h2>
                  <p>{t('beta_signup.header.description')}</p>
                </header>

                <form onSubmit={handleSubmit} className="beta-signup__form">
                  {errorMessage && (
                    <div className="beta-signup__error" role="alert">
                      <AlertCircle size={17} />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <div className="beta-signup__grid">
                    <label>
                      <span>{t('beta_signup.fields.name.label')}</span>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('beta_signup.fields.name.placeholder')}
                        autoComplete="name"
                        required
                      />
                    </label>
                    <label>
                      <span>{t('beta_signup.fields.email.label')}</span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        autoComplete="email"
                        required
                      />
                    </label>
                  </div>

                  <label>
                    <span>{t('beta_signup.fields.device.label')}</span>
                    <select
                      name="deviceType"
                      value={formData.deviceType}
                      onChange={handleInputChange}
                    >
                      <option value="iphone">{t('beta_signup.fields.device.options.iphone')}</option>
                      <option value="android">{t('beta_signup.fields.device.options.android')}</option>
                      <option value="both">{t('beta_signup.fields.device.options.both')}</option>
                      <option value="other">{t('beta_signup.fields.device.options.other')}</option>
                    </select>
                  </label>

                  <label>
                    <span>{t('beta_signup.fields.motivation.label')}</span>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={600}
                      placeholder={t('beta_signup.fields.motivation.placeholder')}
                      required
                    />
                    <small>{formData.motivation.length}/600</small>
                  </label>

                  <label className="beta-signup__terms">
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      required
                    />
                    <span>
                      <Trans
                        i18nKey="beta_signup.fields.terms"
                        components={{
                          terms: <a href="/terms" />,
                          privacy: <a href="/privacy" />,
                        }}
                      />
                    </span>
                  </label>

                  {!isLocal && recaptchaRequired && recaptchaSiteKey && (
                    <div className="beta-signup__captcha">
                      <ReCAPTCHA
                        key={i18n.resolvedLanguage}
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        hl={i18n.resolvedLanguage?.startsWith('es') ? 'es' : 'en'}
                        onChange={setRecaptchaValue}
                        onExpired={() => setRecaptchaValue(null)}
                        theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                      />
                    </div>
                  )}

                  <div className="beta-signup__actions">
                    <p>{t('beta_signup.no_spam')}</p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="beta-signup__primary"
                    >
                      <span>
                        {isSubmitting
                          ? t('beta_signup.actions.sending')
                          : t('beta_signup.actions.submit')}
                      </span>
                      {!isSubmitting && <ArrowRight size={17} />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BetaSignupForm;
